import React, { useEffect, useRef, useState } from "react";
import { Icon, Link } from "components";
import { useAllMenus } from "hooks";
import { flatListToHierarchical } from "utils";
import "./Navigation.scss";
import classNames from "classnames";

const menuItemTierEnglishNames = {
  1: `parent`,
  2: `child`,
  3: `grandchild`,
  4: `great-grandchild`,
};

interface MenuItemInterface {
  path?: string;
  label?: string;
  childItems?: Record<string, any>[]; // replace this with the correct type
  className?: string;
  tier?: number;
  target?: HTMLAnchorElement["target"];
}

const MenuItem = ({
  path,
  label,
  childItems = [],
  className = "menu-item",
  tier = 1, // tracks which tier the menu item is
  target = undefined,
}: MenuItemInterface) => {
  const hasChildren = childItems?.length > 0 ? true : false;
  const [isOpen, setIsOpen] = useState(false);
  const menuItemEl = useRef(null);

  const mouseEnter = () => {
    setIsOpen(true);
  };

  const mouseOut = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    const element = menuItemEl.current;
    if (window.innerWidth > 1080) {
      element.addEventListener("mouseenter", mouseEnter);
      element.addEventListener("mouseleave", mouseOut);
    }
    return () => {
      if (element) {
        element.removeEventListener("mouseenter", mouseEnter);
        element.removeEventListener("mouseleave", mouseOut);
      }
    };
  }, []);

  const removeBodyScroll = () => {
    document.body.classList.remove("hamburger-open");
  };

  return !hasChildren ? (
    <li
      className="menu-item"
      data-nested-tier={tier}
      data-tier-name={menuItemTierEnglishNames[tier]}
      ref={menuItemEl}
      onClick={() => removeBodyScroll()}
    >
      <Link to={path} className="menu-item__link" target={target}>
        {label}
      </Link>
    </li>
  ) : (
    <>
      <li
        // className="menuitem menuitem--open"
        className={classNames({
          "menu-item": true,
          "menu-item--open": isOpen,
        })}
        data-nested-tier={tier}
        data-tier-name={menuItemTierEnglishNames[tier]}
        ref={menuItemEl}
        onClick={() => removeBodyScroll()}
      >
        <Link to={path} className="menu-item__link">
          {label}
        </Link>
        <div onClick={() => setIsOpen((prevState) => !prevState)}>
          <Icon className={className ? `${className}__icon` : ""} name="ArrowDown" color="--brand-secondary" />
        </div>

        <ul className="menu-item__child-list">
          {childItems?.map((childItem) => (
            <MenuItem key={childItem?.id} className={className} {...childItem} tier={tier + 1} />
          ))}
        </ul>
      </li>
    </>
  );
};

export const Navigation = (): JSX.Element => {
  /* First we retrieve our menu */
  const filteredMenu = useAllMenus("PRIMARY_NAVIGATION");
  /* We then need to convert the menu into a hierarchical list */
  const hierarchicalMenu = flatListToHierarchical(filteredMenu?.menuItems?.nodes);

  return (
    <nav className="global-navigation">
      <ul className="global-navigation__list">
        {hierarchicalMenu?.map((menuItem) => (
          <MenuItem key={menuItem.id} {...menuItem} />
        ))}
      </ul>
    </nav>
  );
};
